import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AcceptRingingCallAction, EndCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { SamwinCallListSelector } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.selector';
import { ToggleMuteMicrophoneAction } from 'src/app/store/settings/audio-settings/audio-settings.actions';
import { AudioSettingsState } from 'src/app/store/settings/audio-settings/audio-settings.state';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { AudioDevicesSettingsSelectors } from '../../store/local-storage/local-storage-settings/audio-devices-settings.selectors';
import { SamwinLinesStateSelector } from '../../store/samwin/samwin-lines/samwin-lines.selector';
import { ChangesToHeadsetState } from './changes-to-headset-state.enum';
import { HeadsetDevice } from './headset-device';
import { HeadsetManufacturerType } from './headset-manufacturer-type';

export abstract class AbstractSamwinHeadsetManufacturerService {
   protected destroy$ = new Subject();

   // using behaviorSubject so that the selector element gets the last emitted value needed for indicator
   public attachedHeadset$: BehaviorSubject<Array<HeadsetDevice>>;

   public isHeadsetManufacturerActive$: Observable<boolean>;

   protected constructor(protected store: Store, protected targetHeadsetManufacturer: HeadsetManufacturerType) {
      this.attachedHeadset$ = new BehaviorSubject([]);
   }

   public init() {
      this.isHeadsetManufacturerActive$ = this.store.select(AudioDevicesSettingsSelectors.getHeadsetManufacturer).pipe(
         map((x) => {
            return x === this.targetHeadsetManufacturer || x === HeadsetManufacturerType.ALL;
         })
      );

      this.isHeadsetManufacturerActive$
         .pipe(
            distinctUntilChanged(),
            tap((isManufacturerActive) => {
               try {
                  if (isManufacturerActive) {
                     this.initializeSdk();
                  } else {
                     this.terminateSdk();
                  }
               } catch (error) {
                  console.error('Error during init/terminate sdk.', error);
               }
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinLinesStateSelector.isPhoneRinging).pipe(
                  distinctUntilChanged(),
                  tap((isRinging) => {
                     if (isRinging) {
                        this.changeHeadsetState(ChangesToHeadsetState.IncomingCall);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinLinesStateSelector.getCallDirection).pipe(
                  distinctUntilChanged(),
                  tap((callDirection) => {
                     if (callDirection === IciFlags.OUTBOUND) {
                        this.changeHeadsetState(ChangesToHeadsetState.OutgoingCall);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinLinesStateSelector.isCallInProgress).pipe(
                  distinctUntilChanged(),
                  tap((isCallInProgress) => {
                     if (isCallInProgress) {
                        // unmute all devices when accepting call
                        if (this.store.selectSnapshot(AudioSettingsState.isMicrophoneMuted)) {
                           this.store.dispatch(new ToggleMuteMicrophoneAction());
                        }
                        this.changeHeadsetState(ChangesToHeadsetState.OffHook);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinCallListSelector.getActiveOrRingingCall).pipe(
                  distinctUntilChanged(),
                  tap((gotActiveOrRingingCall) => {
                     if (!gotActiveOrRingingCall) {
                        this.changeHeadsetState(ChangesToHeadsetState.OnHook);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinCallListSelector.getActiveOrRingingCallFromSharedSkill).pipe(
                  distinctUntilChanged(),
                  tap((gotActiveOrRingingCall) => {
                     if (!gotActiveOrRingingCall) {
                        this.changeHeadsetState(ChangesToHeadsetState.OnHook);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(AudioSettingsState.isMicrophoneMuted).pipe(
                  distinctUntilChanged(),
                  tap((isMuted) => {
                     if (isMuted) {
                        this.changeHeadsetState(ChangesToHeadsetState.MuteHeadset);
                     } else {
                        this.changeHeadsetState(ChangesToHeadsetState.UnmuteHeadset);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isHeadsetManufacturerActive$
         .pipe(
            switchMap((isManufacturerActive) => {
               if (!isManufacturerActive) {
                  return of(null);
               }
               return this.store.select(SamwinLinesStateSelector.isSourceLineOnHold).pipe(
                  distinctUntilChanged(),
                  tap((isSourceLineOnHold) => {
                     if (isSourceLineOnHold) {
                        this.changeHeadsetState(ChangesToHeadsetState.Hold);
                     } else {
                        this.changeHeadsetState(ChangesToHeadsetState.Resume);
                     }
                  })
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   protected bringDeviceUpToState(): void {
      console.debug('Bringing device up to state');
      if (this.store.selectSnapshot(SamwinLinesStateSelector.isPhoneRingingOrInProgressOrAnswering)) {
         if (this.store.selectSnapshot(SamwinLinesStateSelector.getCallDirection) === IciFlags.OUTBOUND) {
            this.changeHeadsetState(ChangesToHeadsetState.OutgoingCall);
         } else {
            this.changeHeadsetState(ChangesToHeadsetState.IncomingCall);
         }

         if (this.store.selectSnapshot(SamwinLinesStateSelector.isCallInProgress)) {
            this.changeHeadsetState(ChangesToHeadsetState.OffHook);
         }
      } else {
         this.changeHeadsetState(ChangesToHeadsetState.OnHook);
      }

      if (this.store.selectSnapshot(SamwinLinesStateSelector.isSourceLineOnHold)) {
         this.changeHeadsetState(ChangesToHeadsetState.Hold);
      }

      if (this.store.selectSnapshot(AudioSettingsState.isMicrophoneMuted)) {
         this.changeHeadsetState(ChangesToHeadsetState.MuteHeadset);
      } else {
         this.changeHeadsetState(ChangesToHeadsetState.UnmuteHeadset);
      }
   }

   protected acceptIncomingCall(): void {
      this.store.dispatch(new AcceptRingingCallAction());
   }
   protected endCall(): void {
      this.store.dispatch(new EndCallAction());
   }
   protected toggleMute(): void {
      this.store.dispatch(new ToggleMuteMicrophoneAction());
   }

   protected holdCall(): void {}

   protected resumeMute(): void {}

   protected abstract changeHeadsetState(change: ChangesToHeadsetState): void;

   protected abstract initializeSdk(): void;

   protected abstract terminateSdk(): void;
}
